import { LEGAL_DOCUMENTS, PREFERRED_NAME_MAX_LENGTH } from '@hue/contracts';
import {
  Button,
  Card,
  CardHeader,
  FormErrorSummary,
  Input,
  PasswordStrengthInput,
  PhoneInput,
  Switch,
  useSnackbar,
} from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { KeyRound, MailCheck, Sparkles } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { accountApi } from '../lib/account-api.js';
import { getApiError } from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import { AuthenticatorHelp } from './authenticator-help.js';
import { MfaSetup } from './mfa-qr.js';
import { PortalFooter, PortalMasthead } from './portal-masthead.js';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

function PortalPage({ children }: { children: React.ReactNode }) {
  return (
    <div className="public-portal min-h-screen bg-surface text-content">
      <PortalMasthead />
      <main className="mx-auto max-w-xl px-5 pb-20 pt-10 sm:px-8">{children}</main>
      <PortalFooter />
    </div>
  );
}

/// O cadastro público (ADR 0067, decisão 2). A conta é da pessoa, sem empresa: uma empresa só recebe
/// os dados quando ela pede um horário nela.
export function AccountSignupScreen({ next }: { next?: string | undefined }) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adult, setAdult] = useState(false);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sentTo, setSentTo] = useState<string>();
  const [developmentToken, setDevelopmentToken] = useState<string>();
  const signup = useMutation({
    mutationFn: () =>
      accountApi.signup({
        firstName,
        lastName,
        preferredName: preferredName.trim() || undefined,
        email,
        phone: phone.trim() || undefined,
        adult,
        termsVersion: LEGAL_DOCUMENTS.TERMS.version,
        privacyVersion: LEGAL_DOCUMENTS.PRIVACY.version,
      }),
    onSuccess: (result) => {
      setSentTo(email.trim().toLowerCase());
      setDevelopmentToken(result.developmentToken);
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (firstName.trim().length < 2) found['signup-first-name'] = 'Informe seu nome.';
    if (lastName.trim().length < 2) found['signup-last-name'] = 'Informe seu sobrenome.';
    if (!emailPattern.test(email.trim()))
      found['signup-email'] = 'Informe um e-mail válido: é por ele que chegam as confirmações.';
    if (!adult) found['signup-adult'] = 'A conta é só para quem tem 18 anos ou mais.';
    if (!terms) found['signup-terms'] = 'Para criar a conta, aceite os Termos e a Política.';
    setErrors(found);
    if (Object.keys(found).length === 0) signup.mutate();
  }

  if (sentTo)
    return (
      <PortalPage>
        <Card>
          <CardHeader title="Confira seu e-mail" icon={<MailCheck className="size-4" />} />
          <p className="text-sm leading-6">
            Se for possível criar a conta, enviamos um link de confirmação para{' '}
            <strong>{sentTo}</strong>. Ele vale por 24 horas.
          </p>
          <p className="mt-4 text-sm leading-6 text-content-muted">
            Não chegou? Veja a caixa de spam ou{' '}
            <button
              type="button"
              className="cursor-pointer font-semibold text-brand-600 underline underline-offset-2"
              onClick={() => signup.mutate()}
            >
              reenvie o e-mail
            </button>
            . Endereço errado?{' '}
            <button
              type="button"
              className="cursor-pointer font-semibold text-brand-600 underline underline-offset-2"
              onClick={() => setSentTo(undefined)}
            >
              Corrija aqui
            </button>
            .
          </p>
          {developmentToken ? (
            <Button
              variant="outline"
              className="mt-6"
              onClick={() =>
                void navigate({ to: '/confirm-account', search: { token: developmentToken, next } })
              }
            >
              Abrir o link de confirmação (ambiente de desenvolvimento)
            </Button>
          ) : null}
        </Card>
      </PortalPage>
    );

  return (
    <PortalPage>
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-semibold tracking-tight">Crie sua conta</h1>
        <p className="mt-2 text-sm text-content-muted">
          Uma conta para pedir horários em qualquer empresa do portal.
        </p>
      </div>
      <Card>
        <form noValidate onSubmit={submit} className="space-y-5">
          <p className="text-xs text-content-muted">
            Todos os campos são obrigatórios, exceto os marcados como opcionais.
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              id="signup-first-name"
              label="Nome"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              maxLength={120}
              placeholder="Ana"
              hint="O primeiro nome, ou o composto (Maria Clara)."
              error={errors['signup-first-name']}
            />
            <Input
              id="signup-last-name"
              label="Sobrenome"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              maxLength={120}
              placeholder="Souza Ribeiro"
              hint="Todos os seus sobrenomes, inclusive os do meio."
              error={errors['signup-last-name']}
            />
          </div>
          <Input
            id="signup-preferred-name"
            label="Como você gostaria de ser chamado(a)? (opcional)"
            value={preferredName}
            onChange={(event) => setPreferredName(event.target.value)}
            autoComplete="nickname"
            maxLength={PREFERRED_NAME_MAX_LENGTH}
            placeholder={firstName.trim() || 'Ex.: Cacau'}
            hint="Usamos este nome nas mensagens e no atendimento. Em branco, usamos o seu Nome. Documentos e pagamentos continuam com o nome completo."
          />
          <div className="space-y-2">
            <Input
              id="signup-email"
              label="E-mail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              maxLength={254}
              placeholder="voce@exemplo.com"
              error={errors['signup-email']}
            />
            <p className="rounded-xl border border-brand-500/25 bg-brand-500/5 px-3 py-2 text-xs leading-5">
              Use um e-mail que você acessa.{' '}
              <strong>Todas as confirmações de agendamento chegam por ele</strong>, e só dá para
              pedir horário depois de confirmá-lo.
            </p>
          </div>
          <div className="space-y-2">
            <PhoneInput
              id="signup-phone"
              label="Celular (opcional, recomendado)"
              value={phone}
              onValueChange={setPhone}
              autoComplete="tel"
            />
            <p className="text-xs leading-5 text-content-muted">
              A empresa usa o celular para confirmar seu horário e avisar sobre imprevistos. Você
              decide depois quais novidades quer receber de cada empresa.
            </p>
          </div>
          <div
            className="space-y-2"
            id="signup-adult"
            aria-invalid={Boolean(errors['signup-adult'])}
          >
            <Switch checked={adult} onChange={setAdult}>
              Tenho 18 anos ou mais.
            </Switch>
            {errors['signup-adult'] ? (
              <p role="alert" className="text-xs text-danger">
                {errors['signup-adult']}
              </p>
            ) : null}
          </div>
          <div
            className="space-y-2"
            id="signup-terms"
            aria-invalid={Boolean(errors['signup-terms'])}
          >
            <Switch checked={terms} onChange={setTerms}>
              Li e aceito os Termos de Uso e a Política de Privacidade.
            </Switch>
            <p className="pl-14 text-xs text-content-muted">
              <Link to="/terms" target="_blank" className="underline underline-offset-2">
                Ler os Termos de Uso
              </Link>{' '}
              ·{' '}
              <Link to="/privacy" target="_blank" className="underline underline-offset-2">
                Ler a Política de Privacidade
              </Link>
            </p>
            {errors['signup-terms'] ? (
              <p role="alert" className="text-xs text-danger">
                {errors['signup-terms']}
              </p>
            ) : null}
          </div>
          {signup.error ? (
            <p
              role="alert"
              className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
            >
              {getApiError(signup.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-4">
            <Button type="submit" loading={signup.isPending}>
              Criar minha conta
            </Button>
            <FormErrorSummary />
          </div>
          <p className="text-xs leading-5 text-content-muted">
            Seu cadastro é seu, não de uma empresa. Uma empresa só recebe seus dados quando você
            pede um horário nela.
          </p>
        </form>
      </Card>
      <p className="mt-6 text-center text-sm text-content-muted">
        Já tem conta?{' '}
        <Link to="/login" search={{ next }} className="font-semibold text-brand-600">
          Entrar
        </Link>
      </p>
    </PortalPage>
  );
}

/// Abrir o link do e-mail: criar a senha e, se quiser, ligar o código de segurança (ADR 0067,
/// decisão 7). Ao terminar, a pessoa já está dentro, como cliente.
export function AccountConfirmationScreen({
  token,
  next,
}: {
  token: string;
  next?: string | undefined;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const signup = useQuery({
    queryKey: ['public', 'signup', token],
    queryFn: () => accountApi.inspectSignup(token),
    retry: false,
  });
  const [password, setPassword] = useState('');
  const [mfa, setMfa] = useState(false);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const confirm = useMutation({
    mutationFn: () => accountApi.confirmSignup(token, { password, code: mfa ? code : undefined }),
    onSuccess: async () => {
      await refreshSession(queryClient);
      snackbar.success('Conta ativada. Você já pode pedir horários.');
      await navigate({ to: next ?? '/account', replace: true });
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (
      password.length < 8 ||
      !/[A-Z]/u.test(password) ||
      !/\d/u.test(password) ||
      !/[^A-Za-z0-9]/u.test(password)
    )
      found['confirm-password'] =
        'Use ao menos 8 caracteres, com letra maiúscula, número e símbolo.';
    if (mfa && !/^\d{6}$/u.test(code)) found['confirm-code'] = 'Informe os 6 dígitos.';
    setErrors(found);
    if (Object.keys(found).length === 0) confirm.mutate();
  }
  return (
    <PortalPage>
      {signup.isPending ? (
        <Card className="grid min-h-48 place-items-center">
          <div
            className="size-7 animate-spin rounded-full border-2 border-brand-500/20 border-t-brand-500"
            aria-label="Conferindo o link"
          />
        </Card>
      ) : signup.isError ? (
        <Card>
          <CardHeader title="Link indisponível" icon={<MailCheck className="size-4" />} />
          <p role="alert" className="text-sm text-content-secondary">
            {getApiError(signup.error)}
          </p>
          <Link to="/signup" className="mt-4 inline-block font-semibold text-brand-600">
            Fazer o cadastro de novo
          </Link>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={`Quase lá, ${signup.data.preferredName ?? signup.data.firstName}`}
            icon={<Sparkles className="size-4" />}
          />
          <p className="mb-5 text-sm text-content-muted">
            E-mail confirmado: <strong>{signup.data.email}</strong>. Crie a senha para ativar a
            conta.
          </p>
          <form noValidate onSubmit={submit} className="space-y-5">
            <PasswordStrengthInput
              name="password"
              label="Crie sua senha"
              autoComplete="new-password"
              value={password}
              onValueChange={setPassword}
              error={errors['confirm-password']}
            />
            <Switch checked={mfa} onChange={setMfa}>
              Código de segurança no aplicativo autenticador (opcional)
            </Switch>
            <p className="text-xs leading-5 text-content-muted">
              Pede um código a cada entrada, além da senha. Se um dia você trabalhar numa empresa do
              portal, ele passa a ser obrigatório para entrar como funcionário.
            </p>
            {mfa ? (
              <div className="grid gap-5 sm:grid-cols-[160px_minmax(0,1fr)] sm:items-center">
                <div className="space-y-3">
                  <MfaSetup uri={signup.data.mfaUri} />
                  <div className="flex justify-center">
                    <AuthenticatorHelp />
                  </div>
                </div>
                <Input
                  id="confirm-code"
                  label="Código do aplicativo"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  leftIcon={<KeyRound className="size-4" />}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  error={errors['confirm-code']}
                />
              </div>
            ) : null}
            {confirm.error ? (
              <p
                role="alert"
                className="rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
              >
                {getApiError(confirm.error)}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-4">
              <Button type="submit" loading={confirm.isPending}>
                Ativar minha conta
              </Button>
              <FormErrorSummary />
            </div>
          </form>
        </Card>
      )}
    </PortalPage>
  );
}

/// O link da troca de e-mail: abrir é confirmar. O endereço novo passa a valer na hora.
export function EmailConfirmationScreen({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const confirm = useMutation({
    mutationFn: () => accountApi.confirmEmail(token),
    onSuccess: () => refreshSession(queryClient),
  });
  const { mutate } = confirm;
  useEffect(() => mutate(), [mutate]);
  return (
    <PortalPage>
      <Card>
        <CardHeader title="Troca de e-mail" icon={<MailCheck className="size-4" />} />
        {confirm.isPending || confirm.isIdle ? (
          <p role="status" className="text-sm text-content-muted">
            Confirmando o novo e-mail…
          </p>
        ) : confirm.isError ? (
          <p role="alert" className="text-sm text-danger">
            {getApiError(confirm.error)}
          </p>
        ) : (
          <p role="status" className="text-sm">
            Pronto. O novo e-mail já vale para entrar e para receber as confirmações.
          </p>
        )}
        <Link
          to="/account"
          search={{ tab: 'profile' }}
          className="mt-4 inline-block font-semibold text-brand-600"
        >
          Ir para Meus dados
        </Link>
      </Card>
    </PortalPage>
  );
}
